import React, { useMemo } from "react";
import { Paper, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import * as d3 from "d3";
import { ResizableTableV2 } from "../../../components/resizableTable/ResizableTableV2";
import { dateFormatterMomentV2 } from "../../../util/customFunctions";

const fmtM = (val) => {
  if (val == null || val === "") return "-";
  const m = val / 1000000;
  return d3.format(",.2f")(m);
};

const createColumns = (timeFrame) => {
  const columns = [
    {
      label: "#",
      sortable: false,
      align: "center",
      subColumns: [{
        label: "",
        field: "rowNum",
        type: "number",
        align: "center",
        width: 20,
        minWidth: 5,
        sortable: false,
      }],
      headerBgColor: "#4472c4",
      headerTextColor: "#ffffff",
    },
    {
      label: "Rep",
      sortable: true,
      align: "center",
      subColumns: [{
        label: "",
        field: "rep",
        type: "text",
        align: "left",
        width: 80,
        minWidth: 5,
        sortable: true,
        filterable: true,
      }],
      headerBgColor: "#4472c4",
      headerTextColor: "#ffffff",
    },
    {
      label: "Start Date",
      sortable: true,
      align: "center",
      subColumns: [{
        label: "",
        field: "startDate",
        type: "text",
        align: "right",
        width: 60,
        minWidth: 5,
        sortable: true,
      }],
      headerBgColor: "#4472c4",
      headerTextColor: "#ffffff",
    },
    {
      label: "End Date",
      sortable: true,
      align: "center",
      subColumns: [{
        label: "",
        field: "endDate",
        type: "text",
        align: "right",
        width: 60,
        minWidth: 5,
        sortable: true,
      }],
      headerBgColor: "#4472c4",
      headerTextColor: "#ffffff",
    },
    {
      label: "Avg HFQ+4\nonwards",
      sortable: true,
      align: "center",
      subColumns: [{
        label: "",
        field: "avgQ4",
        type: "number",
        align: "right",
        width: 55,
        minWidth: 5,
        sortable: true,
      }],
      headerBgColor: "#4472c4",
      headerTextColor: "#ffffff",
    },
  ];

  // Add HFQ timeframe columns with alternating colors
  timeFrame.forEach((t, i) => {
    columns.push({
      label: t,
      sortable: false,
      align: "center",
      subColumns: [{
        label: "",
        field: `hfq_${t}`,
        type: "number",
        align: "right",
        width: 50,
        minWidth: 5,
        sortable: false,
      }],
      headerBgColor: i % 2 === 0 ? "#5b9bd5" : "#4471c4",
      headerTextColor: "#ffffff",
    });
  });

  return columns;
};

const transformData = (sortedData, timeFrame) => {
  return sortedData.map((repData, idx) => {
    const startDate = repData.Hire_Date
      ? dateFormatterMomentV2(repData.Hire_Date)
      : "-";
    const endDate = repData.Term_Date
      ? dateFormatterMomentV2(repData.Term_Date)
      : "-";
    const termDateFQ = repData.maxTermHFQ || "-";
    const wondata = repData.wonData || [];

    const row = {
      rowNum: idx + 1,
      rep: repData.rep,
      repRaw: repData.rep,
      startDate,
      endDate,
      isTerminated: endDate !== "-",
      avgQ4: fmtM(repData.avgQ4),
      _hireDateTs: repData.Hire_Date ? new Date(repData.Hire_Date).getTime() : 0,
      _termDateTs: repData.Term_Date ? new Date(repData.Term_Date).getTime() : Infinity,
    };

    timeFrame.forEach((t) => {
      const tfData = wondata.filter((c) => c.type === t);
      const Quota = tfData.length > 0 ? tfData[0].quota : null;
      let timeAcv =
        tfData.length > 0
          ? tfData[0].acv
          : parseInt(t.split("-")[1]) <= parseInt(termDateFQ.split("-")[1])
          ? 0
          : null;
      timeAcv = [undefined].includes(timeAcv) && Quota >= 0 ? 0 : timeAcv;

      const acv = [undefined].includes(timeAcv, Quota)
        ? "-"
        : ["", null].includes(timeAcv)
        ? ""
        : fmtM(timeAcv);

      let timeCount = tfData.length > 0 ? tfData[0].count : null;
      let count = [undefined].includes(timeCount)
        ? "-"
        : ["", null].includes(timeCount)
        ? ""
        : timeCount;

      const cfq = tfData.length > 0 ? tfData[0].closed_fiscal_quarter : null;
      const bgColor = endDate === "-" ? "gray" : "#282727";

      row[`hfq_${t}`] = acv;
      row[`hfq_${t}_count`] = count;
      row[`hfq_${t}_cfq`] = cfq;
      row[`hfq_${t}_bg`] = count === "" ? bgColor : null;
    });

    return row;
  });
};

const RSMRampAnalysisPaper = ({ classes, data, onDrill }) => {
  const isLoading = data === "";
  const hasData = !!data && data !== "" && data !== null;

  const rampData = hasData ? data.data || [] : [];
  const timeFrame = hasData ? data.timeFrame || [] : [];

  // Default sort by Start Date ascending
  const presortedData = useMemo(() => {
    return [...rampData].sort((a, b) => {
      const aDate = a.Hire_Date ? new Date(a.Hire_Date).getTime() : 0;
      const bDate = b.Hire_Date ? new Date(b.Hire_Date).getTime() : 0;
      return aDate - bDate;
    });
  }, [rampData]);

  const columns = useMemo(() => createColumns(timeFrame), [timeFrame]);
  const rows = useMemo(() => transformData(presortedData, timeFrame), [presortedData, timeFrame]);

  const getCellValue = (row, field, rowIndex) => {
    if (field === "rowNum") return rowIndex != null ? rowIndex + 1 : row[field];
    if (field === "rep") {
      return row.isTerminated ? `${row.rep} *` : row.rep;
    }
    return row[field] != null ? row[field] : "";
  };

  const getCellStyle = (row, field) => {
    // Gray out cells with no data (terminated/future)
    if (field.startsWith("hfq_") && !field.endsWith("_count") && !field.endsWith("_cfq") && !field.endsWith("_bg")) {
      const bg = row[`${field}_bg`];
      if (bg) return { backgroundColor: bg, color: bg };
      if (onDrill && row[`${field}_count`] !== "") return { cursor: "pointer" };
    }
    return {};
  };

  const handleRowClick = (row, rowIndex, columnInfo) => {
    if (!onDrill || !columnInfo) return;
    const field = columnInfo.field;
    if (!field.startsWith("hfq_")) return;
    const count = row[`${field}_count`];
    const cfq = row[`${field}_cfq`];
    if (count !== "" && cfq) {
      onDrill({ rep: row.repRaw, quarter: cfq });
    }
  };

  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h5" className={classes.title}>
            Ramp Analysis ($M)
          </Typography>
          <span className={classes.updateFrequency}>Live Data</span>
        </Box>
      </div>

      {isLoading ? (
        <Skeleton variant="rect" animation="wave" height={300} width="100%" />
      ) : !hasData || rows.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="body2" color="textSecondary">No data available</Typography>
        </Box>
      ) : (
        <>
          <ResizableTableV2
            columns={columns}
            rows={rows}
            rowKey="repRaw"
            stickyFirstColumn={true}
            maxWidth="100%"
            getCellValue={getCellValue}
            getCellStyle={getCellStyle}
            onRowClick={onDrill ? handleRowClick : undefined}
            hideSubHeaderRow={true}
            defaultSortField="startDate"
            defaultSortDirection="asc"
          />
          <Box mt={1} px={1}>
            <Typography variant="body2" style={{ fontSize: "0.8rem" }}>
              <span style={{ color: "#ed7d31", fontWeight: 600 }}>*</span> Terminated rep
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default RSMRampAnalysisPaper;
